import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, CalendarDays, Users, Clock, Plus, Trash2, Loader2, Phone, Mail, X,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const TIME_OPTIONS = [];
for (let h = 8; h <= 18; h++) {
  ["00", "30"].forEach((m) => TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:${m}`));
}

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });

const to12h = (t) => {
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-barber-surface border border-barber-gold/20 p-6">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-barber-gold/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-barber-gold" />
      </div>
      <div>
        <div className="font-heading text-3xl text-barber-cream leading-none">{value}</div>
        <div className="text-barber-muted text-xs uppercase tracking-widest mt-1">{label}</div>
      </div>
    </div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_bookings: 0, upcoming_bookings: 0, open_slots: 0 });
  const [bookings, setBookings] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotDate, setSlotDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, b, sl] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/slots"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setSlots(sl.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const toggleTime = (t) =>
    setSelectedTimes((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const addSlots = async () => {
    if (!slotDate || selectedTimes.length === 0) {
      toast.error("Pick a date and at least one time.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/slots", { date: slotDate, times: selectedTimes });
      toast.success(`${data.created} time slot(s) added.`);
      setSelectedTimes([]);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const deleteSlot = async (id) => {
    try {
      await api.delete(`/admin/slots/${id}`);
      toast.success("Slot removed.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const cancelBooking = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/cancel`);
      toast.success("Booking cancelled and slot reopened.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  // group slots by date
  const slotsByDate = slots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-barber-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-barber-gold animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-barber-bg grain relative">
      <header className="border-b border-barber-gold/20 bg-barber-surface">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/barber-parlor-logo.png" alt="The Barber Parlor" className="h-11 w-auto" />
            <span className="hidden sm:inline text-barber-muted text-sm uppercase tracking-widest border-l border-barber-gold/30 pl-3">
              Dashboard
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-barber-muted text-sm">{user?.email}</span>
            <Button
              data-testid="logout-btn"
              onClick={doLogout}
              variant="outline"
              className="rounded-none border-barber-gold/30 bg-transparent text-barber-cream hover:bg-barber-gold/10 hover:text-barber-gold"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <StatCard testid="stat-upcoming" icon={CalendarDays} label="Upcoming Bookings" value={stats.upcoming_bookings} />
          <StatCard testid="stat-total" icon={Users} label="Total Bookings" value={stats.total_bookings} />
          <StatCard testid="stat-open" icon={Clock} label="Open Slots" value={stats.open_slots} />
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="rounded-none bg-barber-surface border border-barber-gold/20">
            <TabsTrigger data-testid="tab-bookings" value="bookings" className="rounded-none data-[state=active]:bg-barber-gold data-[state=active]:text-black">
              Bookings
            </TabsTrigger>
            <TabsTrigger data-testid="tab-availability" value="availability" className="rounded-none data-[state=active]:bg-barber-gold data-[state=active]:text-black">
              Availability
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6">
            <div className="bg-barber-surface border border-barber-gold/20" data-testid="bookings-table">
              {bookings.length === 0 ? (
                <p className="p-8 text-barber-muted">No bookings yet. Add availability so customers can book.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-barber-gold/20 hover:bg-transparent">
                      <TableHead className="text-barber-gold">Date & Time</TableHead>
                      <TableHead className="text-barber-gold">Service</TableHead>
                      <TableHead className="text-barber-gold">Customer</TableHead>
                      <TableHead className="text-barber-gold">Contact</TableHead>
                      <TableHead className="text-barber-gold">Status</TableHead>
                      <TableHead className="text-barber-gold text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((b) => (
                      <TableRow key={b.id} data-testid={`booking-row-${b.id}`} className="border-barber-gold/10 hover:bg-barber-gold/5">
                        <TableCell className="text-barber-cream">
                          {fmtDate(b.date)}<br />
                          <span className="text-barber-gold text-sm">{to12h(b.time)}</span>
                        </TableCell>
                        <TableCell className="text-barber-cream">{b.service_name}</TableCell>
                        <TableCell className="text-barber-cream">{b.customer_name}
                          {b.notes ? <div className="text-barber-muted text-xs mt-1 max-w-[180px]">"{b.notes}"</div> : null}
                        </TableCell>
                        <TableCell className="text-barber-muted text-sm">
                          <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{b.phone}</div>
                          <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{b.email}</div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`rounded-none ${b.status === "cancelled" ? "bg-barber-red/20 text-barber-red" : "bg-barber-gold/15 text-barber-gold"}`}>
                            {b.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {b.status !== "cancelled" && (
                            <Button
                              data-testid={`cancel-booking-${b.id}`}
                              onClick={() => cancelBooking(b.id)}
                              variant="ghost"
                              size="sm"
                              className="rounded-none text-barber-red hover:bg-barber-red/10 hover:text-barber-red"
                            >
                              <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="availability" className="mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-barber-surface border border-barber-gold/20 p-6">
                <h3 className="font-heading text-2xl text-barber-cream mb-4">Add Open Times</h3>
                <label className="text-barber-muted text-sm">Date</label>
                <Input
                  data-testid="slot-date"
                  type="date"
                  value={slotDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setSlotDate(e.target.value)}
                  className="mt-1 mb-5 rounded-none bg-barber-bg border-barber-gold/20 text-barber-cream focus-visible:ring-barber-gold"
                />
                <label className="text-barber-muted text-sm">Select times to open</label>
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2">
                  {TIME_OPTIONS.map((t) => (
                    <button
                      key={t}
                      data-testid={`time-option-${t}`}
                      onClick={() => toggleTime(t)}
                      className={`py-2 text-sm border transition-colors ${
                        selectedTimes.includes(t)
                          ? "border-barber-gold bg-barber-gold/15 text-barber-gold"
                          : "border-barber-gold/20 text-barber-cream hover:border-barber-gold/50"
                      }`}
                    >
                      {to12h(t)}
                    </button>
                  ))}
                </div>
                <Button
                  data-testid="add-slots-btn"
                  onClick={addSlots}
                  disabled={saving}
                  className="mt-6 w-full rounded-none btn-gold font-semibold py-6"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Plus className="w-4 h-4 mr-2" /> Add {selectedTimes.length || ""} Slots</>)}
                </Button>
              </div>

              <div className="bg-barber-surface border border-barber-gold/20 p-6" data-testid="slots-list">
                <h3 className="font-heading text-2xl text-barber-cream mb-4">Scheduled Availability</h3>
                {Object.keys(slotsByDate).length === 0 ? (
                  <p className="text-barber-muted">No availability set yet.</p>
                ) : (
                  <div className="space-y-5 max-h-[520px] overflow-y-auto custom-scroll pr-2">
                    {Object.keys(slotsByDate).sort().map((d) => (
                      <div key={d}>
                        <div className="text-barber-gold text-sm uppercase tracking-widest mb-2">{fmtDate(d)}</div>
                        <div className="flex flex-wrap gap-2">
                          {slotsByDate[d].sort((a, b) => a.time.localeCompare(b.time)).map((s) => (
                            <div
                              key={s.id}
                              className={`flex items-center gap-2 px-3 py-1.5 border text-sm ${
                                s.status === "booked"
                                  ? "border-barber-gold/50 bg-barber-gold/10 text-barber-gold"
                                  : "border-barber-gold/20 text-barber-cream"
                              }`}
                            >
                              {to12h(s.time)}
                              {s.status === "booked" ? (
                                <span className="text-[10px] uppercase">booked</span>
                              ) : (
                                <button
                                  data-testid={`delete-slot-${s.id}`}
                                  onClick={() => deleteSlot(s.id)}
                                  className="text-barber-muted hover:text-barber-red"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
